import { useId, useState } from 'react';
import { Icon } from '@forecastify/ui';
import { useT } from '../../i18n';
import type { EntityHistoryType } from '../../live/topics';
import { EntityHistory } from './entity-history';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A record's history as a section that starts CLOSED (14 Oct 2026).         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"On the contract page the history is a giant log… by default have it in a closed chevron so it
 * only opens when the user actually wants to take a look."* Every history at the foot of a detail
 * page uses this; one that lives in its own TAB (a task's, a project's) does not, because the tab is
 * already the decision to look.
 *
 * ⚠️ **Nothing is fetched until it opens.** A closed section that still read its first page would
 * pay for the giant log on every detail view, which is the half of the complaint nobody sees.
 */
export function CollapsibleHistory({
  type,
  id,
}: {
  readonly type: EntityHistoryType;
  readonly id: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const body = useId();

  return (
    <section>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={body}
        onClick={() => {
          setOpen((current) => !current);
        }}
        className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-[11px] font-medium tracking-wide uppercase transition-colors"
      >
        <Icon
          name="chevron-right"
          size={12}
          aria-hidden
          className={`transition-transform ${open ? 'rotate-90' : ''}`}
        />
        {t('history.title')}
      </button>
      {open ? (
        <div id={body} className="mt-1.5">
          <EntityHistory type={type} id={id} />
        </div>
      ) : null}
    </section>
  );
}
